import axios from 'axios';
import { authService } from './authService';
import config from '../config/environment';

// Address types
export interface Address {
  id?: string;
  addressUuid?: string; // UUID field for backend compatibility
  label: string;
  addressType?: 'home' | 'work' | 'other';
  line1: string;
  line2?: string;
  
  // Store master data IDs - keeping field names for minimal API changes
  state: number;            // Master data state ID
  city: number;             // Master data city ID
  
  // Master data entities (populated by backend for display)
  stateEntity?: {
    stateId: number;
    stateName: string;
    stateCode: string;
    isUnionTerritory: boolean;
  };
  cityEntity?: {
    cityId: number;
    cityName: string;
    stateId: number;
  };
  
  postalCode: string;
  landmark?: string;
  latitude?: string;
  longitude?: string;
  default?: boolean;
  isActive?: boolean;  // Field for soft delete functionality
  
  // Contact information for bookings at this address
  contactName?: string;     // Contact person name
  contactPhone?: string;    // Contact person phone
  
  createdAt?: string;
  updatedAt?: string;
  users?: any; // User details from backend
}

export interface CreateAddressRequest {
  label: string;
  addressType?: 'home' | 'work' | 'other';
  line1: string;
  line2?: string;
  
  // Store ONLY master data IDs - keeping field names as state/city for minimal API changes
  state: number;             // Master data state ID (not text)
  city: number;              // Master data city ID (not text)
  
  postalCode: string;
  landmark?: string;
  latitude?: string;
  longitude?: string;
  default?: boolean;
  isActive?: boolean;  // Field for soft delete functionality
  
  // Contact information for bookings at this address
  contactName?: string;      // Contact person name
  contactPhone?: string;     // Contact person phone
}

export interface AddressResponse {
  addresses: Address[];
  total: number;
}

// Create axios instance for address API calls
const addressApi = axios.create({
  baseURL: config.authServerUrl || 'http://localhost:9000',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Add request interceptor to include access token
addressApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle token refresh
addressApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await authService.refreshAccessToken();
        const token = localStorage.getItem('access_token');
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return addressApi(originalRequest);
        }
      } catch (refreshError) {
        authService.logoutLocal();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export const addressService = {
  createAddress: async (addressData: CreateAddressRequest): Promise<Address> => {
    const response = await addressApi.post('/addresses', addressData);
    return response.data;
  },

  getUserAddresses: async (userUuid: string): Promise<Address[]> => {
    try {
      const apiUrl = `/addresses/user/${userUuid}`;
      console.log('🌐 Fetching from API URL:', apiUrl);
      console.log('🔑 Using userUuid:', userUuid);
      const response = await addressApi.get(apiUrl);
      console.log('✅ Raw API response:', response.data, `(status: ${response.status})`);
      console.log('✅ Response data type:', typeof response.data, Array.isArray(response.data));
      console.log('📡 Response headers:', response.headers);
      
      // Handle both response formats: direct array or wrapped object
      let addresses: Address[];
      if (Array.isArray(response.data)) {
        // API returns array directly: [address1, address2, ...]
        addresses = response.data;
        console.log('📦 Using direct array format');
      } else if (response.data && response.data.addresses) {
        // API returns wrapped object: {addresses: [address1, address2, ...]}
        addresses = response.data.addresses;
        console.log('📦 Using wrapped object format');
      } else {
        addresses = [];
        console.log('⚠️ Unknown response format, defaulting to empty array');
      }
      
      // Convert numeric IDs to strings for compatibility and preserve addressUuid
      const normalizedAddresses = addresses.map(addr => ({
        ...addr,
        id: String(addr.id), // Ensure ID is always a string
        addressUuid: addr.addressUuid || String(addr.id) // Preserve or create addressUuid from id
      }));
      
      console.log('🏠 Final addresses:', normalizedAddresses, `(${normalizedAddresses.length})`);
      console.log('🆔 Address IDs:', normalizedAddresses.map(a => `${a.id} (${typeof a.id})`));
      return normalizedAddresses;
    } catch (error) {
      console.error('❌ API Error:', error);
      if (axios.isAxiosError(error)) {
        console.error('❌ Error response:', error.response?.data);
        console.error('❌ Error status:', error.response?.status);
        if (error.response?.status === 404) {
          return [];
        }
      }
      throw error;
    }
  },

  getCurrentUserAddresses: async (): Promise<Address[]> => {
    const currentUser = await authService.getCurrentUser();
    const userUuid = currentUser.userUuid || currentUser.id;
    console.log('👤 getCurrentUserAddresses - User:', currentUser);
    console.log('🆔 Using userUuid:', userUuid);
    return await addressService.getUserAddresses(userUuid);
  },

  updateAddress: async (addressId: string, addressData: Partial<CreateAddressRequest>): Promise<Address> => {
    const response = await addressApi.put(`/addresses/${addressId}`, addressData);
    return response.data;
  },

  deleteAddress: async (addressId: string): Promise<void> => {
    await addressApi.delete(`/addresses/${addressId}`);
  },

  // Function to update address with isActive and default fields using addressUuid
  updateAddressStatus: async (addressUuid: string, updates: { isActive?: boolean; default?: boolean }): Promise<Address> => {
    console.log('🔄 Updating address status:', addressUuid, updates);
    const response = await addressApi.put(`/addresses/${addressUuid}`, updates);
    return response.data;
  },

  // New function to deactivate address (soft delete) using PUT with addressUuid
  deactivateAddress: async (addressUuid: string): Promise<Address> => {
    console.log('🗑️ Deactivating address:', addressUuid);
    return await addressService.updateAddressStatus(addressUuid, { isActive: false });
  },

  setDefaultAddress: async (addressUuid: string): Promise<Address> => {
    console.log('⭐ Setting default address:', addressUuid);
    return await addressService.updateAddressStatus(addressUuid, { default: true });
  },

  getAddressById: async (addressId: string): Promise<Address> => {
    const response = await addressApi.get(`/addresses/${addressId}`);
    return response.data;
  }
};

export default addressService;